'use client';

/**
 * useListingFilters — URL-state hook for the reusable listing-filter system
 * (BAL-665). Reads/writes filter + sort + search to the URL search params so a
 * filtered view is shareable and back-navigable. Multi-select is serialized as
 * CSV; a param is dropped when it equals its default / is empty. Search is
 * debounced (300ms). Sort + filter are applied by the SURFACE's server loader
 * from this state — this hook is state only, it runs no query.
 *
 * URL plumbing runs on `nuqs` (type-safe parsers, shallow:false so the server
 * loader re-runs on each change). The exported `UseListingFiltersResult` shape
 * is unchanged, so <ListingFilterBar> / <ListingFilterSheet> and every adopter
 * are untouched by the nuqs migration.
 */
import { useCallback, useMemo, useState, useTransition } from 'react';
import {
  debounce,
  parseAsArrayOf,
  parseAsString,
  useQueryStates,
  type ParserBuilder,
} from 'nuqs';

import type {
  AppliedChip,
  FilterFacet,
  ListingFilterConfig,
  ListingFilterState,
} from './types';

const SEARCH_PARAM = 'q';
const SORT_PARAM = 'sort';
const DEBOUNCE_MS = 300;

function facetOf(
  config: ListingFilterConfig,
  key: string,
): FilterFacet | undefined {
  return config.facets.find((f) => f.key === key);
}

function optionLabel(
  config: ListingFilterConfig,
  facetKey: string,
  value: string,
): string {
  const facet = facetOf(config, facetKey);
  if (!facet || facet.kind === 'range') return value;
  return facet.options.find((o) => o.value === value)?.label ?? value;
}

export interface UseListingFiltersResult {
  state: ListingFilterState;
  /** Debounced search value bound to the input (updates immediately in UI). */
  searchInput: string;
  setSearch: (value: string) => void;
  setSort: (value: string) => void;
  /** Replace a facet's whole value array (dropdown/range, or single toggle). */
  setFacet: (key: string, values: string[]) => void;
  /** Toggle one value in/out of a multi-select facet. */
  toggleFacet: (key: string, value: string) => void;
  clearFacet: (key: string) => void;
  clearAll: () => void;
  /** Active filters as removable chips (excludes search + default sort). */
  chips: AppliedChip[];
  /** Count of active facets (for the mobile trigger badge). */
  activeCount: number;
  isPending: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyParser = ParserBuilder<any>;

export function useListingFilters(
  config: ListingFilterConfig,
): UseListingFiltersResult {
  // Stable key so the parser map is rebuilt only when the facet set / default
  // sort actually changes (config is re-derived each render by the surface).
  const facetKeys = config.facets.map((f) => f.key).join('|');

  const parsers = useMemo<Record<string, AnyParser>>(() => {
    const map: Record<string, AnyParser> = {
      [SEARCH_PARAM]: parseAsString.withDefault(''),
      [SORT_PARAM]: parseAsString.withDefault(config.defaultSort),
    };
    for (const facet of config.facets) {
      map[facet.key] = parseAsArrayOf(parseAsString).withDefault([]);
    }
    return map;
    // facetKeys stands in for config.facets (stable string key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetKeys, config.defaultSort]);

  const [isPending, startTransition] = useTransition();
  const [values, setValues] = useQueryStates(parsers, {
    shallow: false, // the surface's server loader must re-run on change
    scroll: false,
    history: 'push',
    startTransition,
  });

  const state = useMemo<ListingFilterState>(() => {
    const facets: Record<string, string[]> = {};
    for (const facet of config.facets) {
      facets[facet.key] = (values[facet.key] as string[] | null) ?? [];
    }
    return {
      search: (values[SEARCH_PARAM] as string | null) ?? '',
      sort: (values[SORT_PARAM] as string | null) ?? config.defaultSort,
      facets,
    };
  }, [values, config]);

  // Immediate input mirror; re-sync when the URL search changes EXTERNALLY
  // (back/forward nav) — adjusting state during render per the React docs.
  const [searchInput, setSearchInput] = useState(state.search);
  const [prevUrlSearch, setPrevUrlSearch] = useState(state.search);
  if (state.search !== prevUrlSearch) {
    setPrevUrlSearch(state.search);
    setSearchInput(state.search);
  }

  const setSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      setValues(
        { [SEARCH_PARAM]: value.trim() || null },
        { limitUrlUpdates: debounce(DEBOUNCE_MS) },
      );
    },
    [setValues],
  );

  const setSort = useCallback(
    (value: string) => {
      // nuqs drops the param when value === the parser default (defaultSort).
      setValues({ [SORT_PARAM]: value });
    },
    [setValues],
  );

  const setFacet = useCallback(
    (key: string, next: string[]) => {
      setValues({ [key]: next.length > 0 ? next : null });
    },
    [setValues],
  );

  const toggleFacet = useCallback(
    (key: string, value: string) => {
      const facet = facetOf(config, key);
      const current = state.facets[key] ?? [];
      const multi = facet?.kind !== 'range' && facet?.multi;
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : multi
          ? [...current, value]
          : [value];
      setFacet(key, next);
    },
    [config, state.facets, setFacet],
  );

  const clearFacet = useCallback(
    (key: string) => setFacet(key, []),
    [setFacet],
  );

  const clearAll = useCallback(() => {
    const patch: Record<string, null> = {
      [SEARCH_PARAM]: null,
      [SORT_PARAM]: null,
    };
    for (const facet of config.facets) patch[facet.key] = null;
    setValues(patch);
    setSearchInput('');
  }, [config.facets, setValues]);

  const chips = useMemo<AppliedChip[]>(() => {
    const out: AppliedChip[] = [];
    for (const facet of config.facets) {
      const facetValues = state.facets[facet.key] ?? [];
      if (facetValues.length === 0) continue;
      if (facet.kind === 'range') {
        const [min, max] = facetValues;
        out.push({
          facetKey: facet.key,
          label: `${facet.label}: ${min ?? ''}–${max ?? ''}${
            facet.unit ? ` ${facet.unit}` : ''
          }`,
        });
      } else {
        for (const value of facetValues) {
          out.push({
            facetKey: facet.key,
            value,
            label: `${facet.label}: ${optionLabel(config, facet.key, value)}`,
          });
        }
      }
    }
    return out;
  }, [config, state.facets]);

  const activeCount = chips.length;

  return {
    state,
    searchInput,
    setSearch,
    setSort,
    setFacet,
    toggleFacet,
    clearFacet,
    clearAll,
    chips,
    activeCount,
    isPending,
  };
}
